import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  setPaperName,
  setPlannerDetails,
  setSelectedType,
  setTestDetails,
} from "../../../redux/exam/examSlice";
import { setCurrentCuetPractice } from "../../../redux/cuet/CuetSlice";
import { navigateFromPracticeOrigin } from "../../../custom_utilities/dashboardBannerNavigation";
import {
  resolvePyqFormatFromName,
  shouldSendFormatParam,
} from "../../../custom_utilities/pyqFormatUtils";

const isCuetPracticeBoardItem = (item) => {
  const itemId = Number(item?.id);
  const name = String(item?.name || "").toLowerCase();
  if ([16, 17, 18].includes(itemId)) return true;
  if (name.includes("board question bank")) return true;
  if (name.includes("cuet question bank")) return true;
  if (name.includes("ncert e-book")) return true;
  return false;
};

export const usePracticeItemNavigation = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const testDetails = useSelector((state) => state.examSlice.testDetails);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSublist, setSelectedSublist] = useState(null);
  const [selectedItemName, setSelectedItemName] = useState("");

  const syncPyqFormatState = useCallback(
    (name) => {
      const pyqFormat = resolvePyqFormatFromName(name);
      if (pyqFormat) {
        dispatch(setSelectedType(pyqFormat));
        dispatch(
          setTestDetails({
            ...(testDetails || {}),
            format: pyqFormat,
          })
        );
        return;
      }

      const nextDetails = { ...(testDetails || {}) };
      delete nextDetails.format;
      dispatch(setTestDetails(nextDetails));
    },
    [dispatch, testDetails]
  );

  const navigateToCuetBoard = useCallback(
    (item) => {
      dispatch(
        setCurrentCuetPractice({
          id: item?.id,
          name: item?.name,
        })
      );
      navigateFromPracticeOrigin(history, "/cuet+board");
    },
    [dispatch, history]
  );

  const handleModalItemClick = useCallback(
    (subitem) => {
      setModalOpen(false);

      if (isCuetPracticeBoardItem(selectedSublist)) {
        navigateToCuetBoard(selectedSublist);
        return;
      }

      if (
        String(selectedItemName || "")
          .toLowerCase()
          .includes("adaptive question bank")
      ) {
        navigateFromPracticeOrigin(history, "/practice/adaptive-question-bank", {
          item: subitem,
        });
        return;
      }

      syncPyqFormatState(selectedItemName);

      dispatch(
        setPlannerDetails({
          planner_id: subitem.planner_id,
          planner_type_id: subitem.planner_type_id,
          category_id: subitem.category_id || "",
          id: subitem.id || "",
          type: subitem?.type?.toLowerCase(),
        })
      );

      const type = subitem?.type?.toLowerCase();

      if (type === "year wise") {
        navigateFromPracticeOrigin(history, "/practice/paper-listing?type=Year Wise");
      } else if (type === "topic wise") {
        navigateFromPracticeOrigin(history, "/practice/topic-wise");
      } else {
        navigateFromPracticeOrigin(history, "/practice/paper-listing");
      }
    },
    [
      dispatch,
      history,
      navigateToCuetBoard,
      selectedItemName,
      selectedSublist,
      syncPyqFormatState,
    ]
  );

  const handlePracticeItemClick = useCallback(
    (item) => {
      const practiceItem = item?.raw ?? item;
      const section = item?.section ?? "main_practice_dashboard";

      dispatch(setPaperName(practiceItem.name));
      const itemName = String(practiceItem?.name || "").toLowerCase();
      const isAdaptiveQuestionBank = itemName.includes("adaptive question bank");
      const isUnlimitedQuestionBank = itemName.includes("unlimited question bank");

      if (isAdaptiveQuestionBank) {
        navigateFromPracticeOrigin(history, "/practice/adaptive-question-bank", {
          item: practiceItem,
        });
        return;
      }

      if (isUnlimitedQuestionBank) {
        navigateFromPracticeOrigin(history, "/question_bank");
        return;
      }

      if (itemName.includes("homework")) {
        navigateFromPracticeOrigin(history, "/homework");
        return;
      }

      if (itemName.includes("mistake book")) {
        navigateFromPracticeOrigin(history, "/practice/mistake-book");
        return;
      }

      if (practiceItem?.type) {
        dispatch(setSelectedType(practiceItem.type));
        if (shouldSendFormatParam(practiceItem.type)) {
          dispatch(
            setTestDetails({
              ...testDetails,
              format: practiceItem.type,
            })
          );
        } else {
          const nextDetails = { ...testDetails };
          delete nextDetails.format;
          dispatch(setTestDetails(nextDetails));
        }
      }

      if (isCuetPracticeBoardItem(practiceItem)) {
        navigateToCuetBoard(practiceItem);
        return;
      }

      if (itemName.includes("pyq")) {
        if (practiceItem.sublist) {
          setSelectedSublist(practiceItem);
          setSelectedItemName(practiceItem.name);
          setModalOpen(true);
        }
        return;
      }

      if (
        practiceItem?.sublist &&
        Array.isArray(practiceItem.sublist) &&
        practiceItem.sublist.length > 0
      ) {
        setSelectedSublist(practiceItem);
        setSelectedItemName(practiceItem.name);
        setModalOpen(true);
        return;
      }

      if (itemName.includes("adaptive question bank")) {
        navigateFromPracticeOrigin(history, "/practice/adaptive-question-bank");
        return;
      }

      if (section === "main_practice_dashboard") {
        const name = practiceItem?.name?.toLowerCase();
        const itemId = Number(practiceItem?.id);
        const isExamCrackerMain =
          itemId === 9 || name.includes("exam cracker (jee mains)");
        const isExamCrackerAdvanced =
          itemId === 8 ||
          name.includes("exam cracker (jee advanced)") ||
          name.includes("exam cracker (jee advance)");
        if (isExamCrackerMain) {
          const paperTypeId = itemId || 9;
          dispatch(setSelectedType("main"));
          dispatch(
            setTestDetails({
              ...(testDetails || {}),
              format: "main",
            })
          );
          dispatch(
            setPlannerDetails({
              planner_id: practiceItem?.planner_id || "",
              planner_type_id: practiceItem?.planner_type_id || "",
              category_id: paperTypeId,
              id: paperTypeId,
              type: "topic wise",
            })
          );
          navigateFromPracticeOrigin(history, "/practice/topic-wise");
          return;
        }
        if (isExamCrackerAdvanced) {
          const paperTypeId = itemId || 8;
          dispatch(setSelectedType("adv"));
          dispatch(
            setTestDetails({
              ...(testDetails || {}),
              format: "adv",
            })
          );
          dispatch(
            setPlannerDetails({
              planner_id: practiceItem?.planner_id || "",
              planner_type_id: practiceItem?.planner_type_id || "",
              category_id: paperTypeId,
              id: paperTypeId,
              type: "topic wise",
            })
          );
          navigateFromPracticeOrigin(history, "/practice/topic-wise");
          return;
        }
        const isExamCrackerNeet =
          itemId === 6 ||
          (name.includes("exam cracker") && name.includes("neet"));
        if (isExamCrackerNeet) {
          const paperTypeId = 6;
          dispatch(setSelectedType("main"));
          dispatch(
            setTestDetails({
              ...(testDetails || {}),
              format: "main",
            })
          );
          dispatch(
            setPlannerDetails({
              planner_id: practiceItem?.planner_id || "",
              planner_type_id: practiceItem?.planner_type_id || "",
              category_id: paperTypeId,
              id: paperTypeId,
              type: "topic wise",
            })
          );
          navigateFromPracticeOrigin(history, "/practice/topic-wise");
          return;
        }
      }

      if (section === "enhance_your_practice") {
        const name = itemName;
        if (name.includes("homework")) {
          navigateFromPracticeOrigin(history, "/homework");
          return;
        }
        if (name.includes("mistake book")) {
          navigateFromPracticeOrigin(history, "/practice/mistake-book");
          return;
        }
        if (name.includes("conceptual problem sheets")) {
          navigateFromPracticeOrigin(history, "/cps");
          return;
        }
        if (name.includes("adaptive cps")) {
          navigateFromPracticeOrigin(history, "/create-adaptive-cps");
          return;
        }
        if (name.includes("exams")) {
          navigateFromPracticeOrigin(history, "/exam");
          return;
        }
        if (name.includes("bitsat")) {
          syncPyqFormatState(practiceItem.name);
          dispatch(
            setPlannerDetails({
              planner_id: practiceItem.planner_id,
              planner_type_id: practiceItem.planner_type_id,
              category_id: practiceItem.category_id || "",
              id: practiceItem.id || "",
              type: practiceItem.type || "",
            })
          );
          navigateFromPracticeOrigin(history, "/practice/paper-listing");
          return;
        }
        if (name.includes("ncert")) {
          navigateFromPracticeOrigin(history, "/ncert");
          return;
        }
        if (name.includes("adaptive question bank")) {
          navigateFromPracticeOrigin(history, "/practice/adaptive-question-bank");
          return;
        }
        if (name.includes("mock")) {
          syncPyqFormatState(practiceItem.name);
          dispatch(
            setPlannerDetails({
              planner_id: practiceItem.planner_id,
              planner_type_id: practiceItem.planner_type_id,
              category_id: practiceItem.category_id || "",
              id: practiceItem.id || "",
              type: practiceItem.type || "",
            })
          );
          navigateFromPracticeOrigin(history, "/practice/paper-listing");
          return;
        }
        if (name.includes("unlimited question bank")) {
          navigateFromPracticeOrigin(history, "/question_bank");
          return;
        }
      }

      if (practiceItem?.planner_id && practiceItem?.planner_type_id) {
        syncPyqFormatState(practiceItem.name);
        dispatch(
          setPlannerDetails({
            planner_id: practiceItem.planner_id,
            planner_type_id: practiceItem.planner_type_id,
            category_id: practiceItem.category_id || "",
            id: practiceItem.id || "",
            type: practiceItem.type || "",
          })
        );

        if (section === "main_practice_dashboard") {
          navigateFromPracticeOrigin(history, "/practice/paper-listing");
        } else {
          navigateFromPracticeOrigin(history, "/subjects");
        }
        return;
      }

      navigateFromPracticeOrigin(history, "/practice/paper-listing");
    },
    [
      dispatch,
      history,
      navigateToCuetBoard,
      syncPyqFormatState,
      testDetails,
    ]
  );

  const closeModal = useCallback(() => setModalOpen(false), []);

  return {
    handlePracticeItemClick,
    handleModalItemClick,
    closeModal,
    modalOpen,
    selectedSublist,
    selectedItemName,
  };
};
